import React, { Activity } from 'react';
import { FormProvider } from 'react-hook-form';
import { StyleSheet } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';

import { Divider, Stack } from '@/components/layout';
import { STRINGS } from '@/constants/strings';
import MutatePlayerView, {
  GENDER_OPTIONS,
  LEVEL_OPTIONS,
} from '@/features/player/components/mutate-player';
import type { PlayerProfileDoc, PlayerProgressItem } from '@/features/player/components/types';
import { useMutatePlayer } from '@/features/player/hooks/use-mutate-player';
import { usePlayerProfileQuery } from '@/features/player/queries';
import { createStyles, useScaling } from '@/hooks';
import { useBackPress } from '@/hooks/use-back-press';
import type { AuthStackParamList } from '@/navigations/types';
import { colors } from '@/theme/colors';
import { RouteProp, useRoute } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

/**
 * TODO: Need refactor this screen to use MutateInfoTemplate
 */
export const MutatePlayerScreen = () => {
  const { scale, isTablet } = useScaling();
  const insets = useSafeAreaInsets();
  const styles = useStyles();
  const sectionGap = scale(24);
  const route = useRoute<RouteProp<AuthStackParamList, 'MutatePlayer'>>();
  const mode = route.params?.mode ?? 'create';
  const playerId = route.params?.playerId;
  const deleteReturnHomeScreen = route.params?.deleteReturnHomeScreen;
  const { form, onSave, onCancel, onDelete } = useMutatePlayer({
    mode,
    playerId,
    deleteReturnHomeScreen,
  });

  const { data: profileData } = usePlayerProfileQuery(
    mode === 'edit' && playerId ? playerId : undefined,
  );

  const playerProgress = React.useMemo((): PlayerProgressItem[] => {
    return profileData?.progress ?? [];
  }, [profileData]);

  const firstName = form.watch('firstName');
  const lastName = form.watch('lastName');

  const playerDisplayName = React.useMemo(() => {
    const p = profileData as PlayerProfileDoc | null | undefined;
    const fromDoc =
      (p?.name && String(p.name).trim()) ||
      [p?.firstname, p?.lastname]
        .filter(Boolean)
        .map(s => String(s).trim())
        .join(' ')
        .trim();
    if (fromDoc) return fromDoc;
    const fromForm = [firstName, lastName].filter(Boolean).join(' ').trim();
    return fromForm || STRINGS.player.mutatePlayer.levelTab.removeProgressPlayerFallback;
  }, [profileData, firstName, lastName]);

  useBackPress(() => {
    onCancel();
    return true;
  });

  return (
    <FormProvider {...form}>
      <Stack flex={1} backgroundColor={isTablet ? colors['1'] : 'white'}>
        <MutatePlayerView.Header
          title={mode === 'edit' ? STRINGS.player.mutatePlayer.header.editTitle : undefined}
          onDelete={isTablet ? onDelete : undefined}
        />
        <Divider color={colors['5']} spacing={scale(0)} thickness={StyleSheet.hairlineWidth} />

        <KeyboardAwareScrollView
          style={styles.scroll}
          contentContainerStyle={[styles.formContent, isTablet && styles.formContentTablet]}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          bottomOffset={scale(12)}
          extraKeyboardSpace={scale(12)}>
          <Stack
            width="100%"
            maxWidth={isTablet ? scale(676) : undefined}
            gap={sectionGap}
            alignSelf="center">
            {mode === 'edit' ? (
              <MutatePlayerView.EditTabs
                playerId={playerId ?? ''}
                playerDisplayName={playerDisplayName}
                progressItems={playerProgress}
                initialProfileLevelValue={
                  profileData?.level != null && String(profileData.level).trim() !== ''
                    ? `level_${Number(profileData.level)}`
                    : undefined
                }
                profileContent={
                  <>
                    <MutatePlayerView.ProfileSection
                      genderOptions={GENDER_OPTIONS}
                      levelOptions={LEVEL_OPTIONS}
                      hideLevelField
                    />

                    <Activity mode={!isTablet ? 'visible' : 'hidden'}>
                      <Divider
                        color={colors['5']}
                        spacing={0}
                        thickness={StyleSheet.hairlineWidth}
                      />
                    </Activity>

                    <MutatePlayerView.ContactSection />

                    <MutatePlayerView.ClubSection />
                  </>
                }
              />
            ) : (
              <>
                <MutatePlayerView.ProfileSection
                  genderOptions={GENDER_OPTIONS}
                  levelOptions={LEVEL_OPTIONS}
                />

                <Activity mode={!isTablet ? 'visible' : 'hidden'}>
                  <Divider color={colors['5']} spacing={0} thickness={StyleSheet.hairlineWidth} />
                </Activity>

                <MutatePlayerView.ContactSection />

                <MutatePlayerView.ClubSection />
              </>
            )}
          </Stack>
        </KeyboardAwareScrollView>

        <MutatePlayerView.Footer
          isTablet={isTablet}
          insetBottom={insets.bottom}
          onSave={onSave}
          onCancel={onCancel}
          onDelete={onDelete}
          saveDisabled={false}
          mode={mode}
        />
      </Stack>
    </FormProvider>
  );
};

const useStyles = createStyles(({ scale, isTablet }) => ({
  scroll: {
    flex: 1,
    backgroundColor: isTablet ? colors['1'] : 'white',
  },
  formContent: {
    paddingHorizontal: scale(18),
    paddingTop: scale(16),
    paddingBottom: scale(20),
  },
  formContentTablet: {
    paddingHorizontal: scale(16),
    alignItems: 'center',
  },
}));
